import { DialogConfirm } from '../elements/DialogConfirm';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { ButtonCustom } from '../elements/ButtonCustom';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { DatePicker } from '../elements/DatePicker';
import { useFilteredRoomContext } from 'hooks/useFilteredRoomContext';
import { InputSelect } from '../elements/InputSelect';
import { InputText } from '../elements/InputText';
import { Box, Stack, IconButton, Typography } from '@mui/material';
import { lowerCase, startCase } from 'lodash';
import { convertFileToStorage, getNameOption } from 'utils/convertData';
import { dropdownValueTypes } from './ModalStartInspection';
import { SelectChangeEvent } from '@mui/material/Select';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import { RoomLevelIssuePhotos } from 'types/room';
import { useParams } from 'react-router-dom';
import { entriesInclude } from 'utils/entries';

type SingleRoomLevelIssue = {
  id?: string;
  issueTypeName?: string;
  actionBy?: string;
  validated?: string;
  actionByDate?: string;
  description?: string;
  date?: string;
  files?: File[];
  /** Soft-archive existing `roomLevelIssuePhotos` junction ids (same as app / wall photos). */
  archivePhotoIds?: number[];
  /* eslint-disable @typescript-eslint/no-explicit-any */
  [key: string]: any;
};

const RoomLevelIssueSchema = yup.object({
  issueTypeName: yup.string().optional(),
  actionBy: yup.string().optional(),
  actionByDate: yup.string().optional(),
  description: yup.string().required(),
  date: yup.string().optional(),
  files: yup.array().of(yup.mixed<File>()).optional(),
  archivePhotoIds: yup.array().of(yup.number()).optional()
});

type ModalCreateRoomLevelIssueProps = {
  open: boolean;
  /* eslint-disable @typescript-eslint/no-explicit-any */
  initValue?: any;
  onClose: () => void;
  /* eslint-disable @typescript-eslint/no-explicit-any */
  onCreate: (payload: any) => void;
  onDeleteIssue?: (params: { id: number; submitId: number }) => Promise<void>;
};

export const ModalCreateRoomLevelIssue = ({
  open,
  onClose,
  onCreate,
  initValue,
  onDeleteIssue
}: ModalCreateRoomLevelIssueProps) => {
  const { clientId } = useParams();
  const [deleting, setDeleting] = React.useState(false);
  const { systemDropdownValues, singleEntryField, users } = useFilteredRoomContext(clientId);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const roomLevelIssueOption = useMemo(() => {
    return systemDropdownValues?.filter((item) =>
      item.options.some((opt) => opt.type === dropdownValueTypes.roomLevelIssue)
    );
  }, [systemDropdownValues]);

  const roomLevelIssueOptionSingleField = useMemo(() => {
    return singleEntryField?.filter((item) =>
      entriesInclude(item.entries, dropdownValueTypes.roomLevelIssue)
    );
  }, [singleEntryField]);
  const { control, watch, setValue, reset, handleSubmit, getValues } =
    useForm<SingleRoomLevelIssue>({
      resolver: yupResolver(RoomLevelIssueSchema) as any,
      defaultValues: {
        issueType: '',
        issueTypeName: '',
        description: '',
        date: '',
        actionBy: '',
        files: [],
        archivePhotoIds: []
      }
    });

  const hydrateFormFromInitValue = useCallback(() => {
    if (!initValue) return;
    const roomOptions: any = {
      description: initValue.description || '',
      id: initValue.id || '',
      status: initValue.status || '',
      resolutionDate: initValue.resolutionDate || null,
      validated: initValue.validated ? 'Yes' : 'No',
      actionBy: initValue.actionBy || '',
      actionByDate: initValue.actionByDate || '',
      files: [],
      archivePhotoIds: [],
      existingPhotos: initValue.roomLevelIssuePhotos || (initValue.photo ? [initValue.photo] : [])
    };

    initValue.options?.forEach((opt: any) => {
      if (opt.fieldType === 'DROPDOWN') {
        roomOptions[`option_${opt.type}`] = opt.value;
      } else {
        roomOptions[`single_${opt.type}`] = opt.value;
      }
    });
    reset(roomOptions);
  }, [initValue, reset]);

  useEffect(() => {
    if (!open) {
      setIsEditing(false);
    }
  }, [open]);

  useEffect(() => {
    if (open && initValue) {
      hydrateFormFromInitValue();
    }
    if (!open) {
      const currentValues = getValues();
      const resetObj: any = {
        issueType: '',
        issueTypeName: '',
        description: '',
        date: '',
        actionBy: '',
        validated: '',
        actionByDate: '',
        status: '',
        files: [],
        archivePhotoIds: []
      };

      Object.keys(currentValues).forEach((key) => {
        if (key.startsWith('option_')) {
          resetObj[key] = '';
        }
        if (key.startsWith('single_')) {
          resetObj[key] = '';
        }
      });

      reset(resetObj, { keepDefaultValues: false });
    }
  }, [open, initValue, reset, hydrateFormFromInitValue, getValues]);

  const onSubmit = (value: SingleRoomLevelIssue) => {
    const payload = {
      ...value,
      date: value.date ? dayjs(value.date).toISOString() : undefined
    };

    onCreate(payload);
    onClose();
  };

  const isEditMode = Boolean(initValue?.id);
  const formLocked = isEditMode && !isEditing;
  const submitIdNum = initValue?.submitId != null ? Number(initValue.submitId) : NaN;
  const issueIdNum = initValue?.id != null ? Number(initValue.id) : NaN;

  const handleCancelEdit = () => {
    setIsEditing(false);
    hydrateFormFromInitValue();
  };

  const handleDeleteIssue = async () => {
    if (!onDeleteIssue || !isEditMode || Number.isNaN(issueIdNum) || Number.isNaN(submitIdNum)) {
      return;
    }
    const ok = window.confirm(
      'Are you sure you want to delete this location level issue? This action cannot be undone.'
    );
    if (!ok) return;
    setDeleting(true);
    try {
      await onDeleteIssue({ id: issueIdNum, submitId: submitIdNum });
      onClose();
    } catch {
      /* parent shows flash / error */
    } finally {
      setDeleting(false);
    }
  };

  const handleRemoveExistingPhoto = (id: number) => {
    const currentArchiveIds = getValues('archivePhotoIds') || [];
    setValue('archivePhotoIds', [...currentArchiveIds, id]);
    const currentExisting = getValues('existingPhotos' as any) || [];
    setValue(
      'existingPhotos' as any,
      currentExisting.filter((p: any) => p.id !== id)
    );
  };

  return (
    <DialogConfirm
      textTitle={!initValue?.id ? 'Create Location Level Issue' : 'Update Location Level Issue'}
      maxWidth={600}
      isOpen={open}
      onClose={onClose}>
      <Box
        sx={{
          width: '100%',
          border: '1px solid #E0E0E0',
          borderRadius: 2,
          p: 2,
          mb: 3,
          position: 'relative'
        }}>
        <Stack gap={2}>
          {roomLevelIssueOption?.map((opt) => (
            <React.Fragment key={opt.id}>
              <Controller
                control={control}
                name={`option_${opt.id}`}
                render={({ field: { onChange, value }, fieldState }) => (
                  <InputSelect
                    disabled={formLocked}
                    title={`${startCase(lowerCase(getNameOption(opt as any)))}:`}
                    inputError={fieldState.error?.message}
                    value={value}
                    placeholder="Select Issue Type"
                    onChange={(val) => {
                      onChange(val);
                    }}
                    options={opt?.dropdownValues?.map((item) => ({
                      label: item.value,
                      value: item.value
                    }))}
                  />
                )}
              />

              {watch(`option_${opt.id}`) === 'OTHER' && (
                <Controller
                  name={`option_${opt.id}_name`}
                  control={control}
                  render={({ field, fieldState }) => (
                    <InputText
                      title="Other Issue"
                      placeholder="Enter Other Issue"
                      inputError={fieldState.error?.message}
                      inputProps={{ ...field, disabled: formLocked }}
                    />
                  )}
                />
              )}
            </React.Fragment>
          ))}
          {roomLevelIssueOptionSingleField?.map((field) => (
            <React.Fragment key={field.id}>
              {field.type === 'DATE' ? (
                <Controller
                  name={`single_${field.id}`}
                  control={control}
                  render={({ field: f }) => (
                    <DatePicker
                      disabled={formLocked}
                      title={`${field.name}:`}
                      onChange={(val) =>
                        setValue(`single_${field.id}`, val ? dayjs(val).format('YYYY-MM-DD') : '')
                      }
                      value={f.value ? dayjs(f.value) : undefined}
                    />
                  )}
                />
              ) : (
                <Controller
                  name={`single_${field.id}`}
                  control={control}
                  render={({ field: f, fieldState }) => (
                    <InputText
                      title={`${field.name}:`}
                      placeholder={`Enter ${field.name}`}
                      inputProps={{ ...f, disabled: formLocked }}
                      inputError={fieldState.error?.message}
                    />
                  )}
                />
              )}
            </React.Fragment>
          ))}
          <Controller
            name={`description`}
            control={control}
            render={({ field, fieldState }) => (
              <InputText
                title="Description"
                placeholder="Enter Description"
                inputError={fieldState.error?.message}
                inputProps={{
                  ...field,
                  multiline: true,
                  rows: 5,
                  style: { padding: '0px' },
                  disabled: formLocked
                }}
              />
            )}
          />
          <Controller
            control={control}
            render={({ field: { onChange, value }, fieldState }) => (
              <InputSelect
                disabled={formLocked}
                title={'Status'}
                inputError={fieldState?.error?.message || ''}
                value={value}
                placeholder={'Status'}
                onChange={(e: SelectChangeEvent<unknown>) => {
                  onChange(e.target.value);
                  if (e.target.value === 'RESOLVED') {
                    setValue(`resolutionDate`, dayjs().toISOString());
                  }
                }}
                options={[
                  { value: 'ACTIVE', label: 'Open' },
                  { value: 'RESOLVED', label: 'Resolved' },
                  { value: 'ARCHIVED', label: 'Archived' }
                ]}
              />
            )}
            name={`status`}
          />
          {watch('status') === 'RESOLVED' && (
            <Controller
              name={'resolutionDate'}
              control={control}
              render={({ field: f }) => (
                <DatePicker
                  disabled={formLocked}
                  includeTime
                  title={'Resolution date'}
                  onChange={(val) =>
                    setValue(`resolutionDate`, val ? dayjs(val).toISOString() : '')
                  }
                  value={f.value ? dayjs(f.value) : undefined}
                />
              )}
            />
          )}
          <Controller
            name={`actionBy`}
            control={control}
            render={({ field, fieldState }) => (
              <InputSelect
                disabled={formLocked}
                title="Action By:"
                placeholder="Select User"
                value={field.value}
                inputError={fieldState.error?.message}
                onChange={field.onChange}
                options={users?.map((item) => ({
                  label: item.label,
                  value: item.label
                }))}
              />
            )}
          />
          <Controller
            control={control}
            render={({ field: { value }, fieldState }) => (
              <DatePicker
                disabled={formLocked}
                inputError={fieldState?.error?.message || ''}
                style={{ flexDirection: 'column' }}
                title={'Choose Action By Date'}
                onChange={(val) => setValue('actionByDate', val ? dayjs(val).toISOString() : '')}
                value={value ? dayjs(value) : undefined}
              />
            )}
            name={'actionByDate'}
          />
          {/* PHOTO UPLOAD */}
          {/* PHOTO UPLOAD MULTIPLE */}
          <Stack style={{ width: '100%', marginBottom: '10px' }}>
            <Typography sx={{ fontSize: 14, fontWeight: 500, marginBottom: '4px' }}>
              Upload Photos
            </Typography>
            <input
              accept={'image/png, image/jpeg , image/jpg'}
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              multiple
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const files = e.target.files;
                if (files && files.length > 0) {
                  setValue('files', [...(getValues('files') || []), ...Array.from(files)]);
                }
                e.target.value = '';
              }}
            />
            {/* Photos List (Existing & New) */}
            <Stack gap={2} mb={2}>
              {watch('existingPhotos')?.map((item: RoomLevelIssuePhotos) => {
                return (
                  <Box
                    key={item.id}
                    sx={{
                      position: 'relative',
                      border: '1px solid #ddd',
                      borderRadius: 2,
                      overflow: 'hidden'
                    }}>
                    <img
                      src={convertFileToStorage('inspections', item.photo)}
                      alt="existing"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <IconButton
                      size="small"
                      disabled={formLocked}
                      onClick={() => handleRemoveExistingPhoto(item.id)}
                      sx={{
                        position: 'absolute',
                        top: 2,
                        right: 2,
                        bgcolor: 'rgba(255, 255, 255, 0.8)',
                        padding: '2px',
                        '&:hover': { bgcolor: 'white' }
                      }}>
                      <DeleteIcon sx={{ fontSize: 26 }} />
                    </IconButton>
                  </Box>
                );
              })}

              {watch('files')?.map((file: File, index: number) => (
                <Box
                  key={index}
                  sx={{
                    position: 'relative',
                    border: '1px solid #ddd',
                    borderRadius: 2,
                    overflow: 'hidden'
                  }}>
                  <img
                    src={URL.createObjectURL(file)}
                    alt="new"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                  <IconButton
                    size="small"
                    disabled={formLocked}
                    onClick={() => {
                      const currentFiles = getValues('files') || [];
                      setValue(
                        'files',
                        currentFiles.filter((_, i) => i !== index)
                      );
                    }}
                    sx={{
                      position: 'absolute',
                      top: 2,
                      right: 2,
                      bgcolor: 'rgba(255, 255, 255, 0.8)',
                      padding: '2px',
                      '&:hover': { bgcolor: 'white' }
                    }}>
                    <DeleteIcon sx={{ fontSize: 26 }} />
                  </IconButton>
                </Box>
              ))}
            </Stack>

            <Box
              onClick={() => {
                if (!formLocked) fileInputRef.current?.click();
              }}
              onDragOver={(e) => {
                if (formLocked) return;
                e.preventDefault();
                if (!isDragging) setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={(e) => {
                if (formLocked) return;
                e.preventDefault();
                setIsDragging(false);
                const files = e.dataTransfer.files;
                if (files && files.length > 0) {
                  setValue('files', [...(getValues('files') || []), ...Array.from(files)]);
                }
              }}
              sx={{
                width: '100%',
                height: 120,
                border: '1px dashed #ccc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: formLocked ? 'not-allowed' : 'pointer',
                borderRadius: '4px',
                bgcolor: isDragging ? '#e3f2fd' : '#f5f5f5',
                transition: 'background-color 0.2s',
                opacity: formLocked ? 0.55 : 1,
                pointerEvents: formLocked ? 'none' : 'auto'
              }}>
              <UploadFileIcon sx={{ color: isDragging ? '#1976d2' : '#888' }} />
            </Box>
          </Stack>
        </Stack>
      </Box>

      <Stack sx={{ marginTop: '30px', width: '100%', alignItems: 'center' }} gap={2}>
        {!isEditMode && (
          <Stack direction="row" gap={2.5} justifyContent="center" width="100%">
            <ButtonCustom
              label={'Submit'}
              sx={{ width: 200 }}
              disabled={deleting}
              onClick={handleSubmit(onSubmit)}
            />
          </Stack>
        )}
        {isEditMode && !isEditing && (
          <ButtonCustom
            label={'Edit'}
            sx={{ width: 200 }}
            disabled={deleting}
            onClick={() => setIsEditing(true)}
          />
        )}
        {isEditMode && isEditing && (
          <Stack gap={2} alignItems="center" width="100%">
            <Stack direction="row" gap={2.5} flexWrap="wrap" justifyContent="center">
              <ButtonCustom
                isSecondary
                label={'Cancel'}
                sx={{ width: 200 }}
                disabled={deleting}
                onClick={handleCancelEdit}
              />
              <ButtonCustom
                label={'Save'}
                sx={{ width: 200 }}
                disabled={deleting}
                onClick={handleSubmit(onSubmit)}
              />
            </Stack>
            {onDeleteIssue && (
              <ButtonCustom
                isDelete
                sx={{ width: 200 }}
                label={deleting ? 'Deleting…' : 'Delete'}
                disabled={deleting}
                onClick={() => void handleDeleteIssue()}
              />
            )}
          </Stack>
        )}
      </Stack>
    </DialogConfirm>
  );
};
